/**
 * Composable Report Widget — type contracts.
 * Co-located with the widget that implements them.
 * See scratch/COMPOSABLE_REPORT_WIDGET_PLAN.md §4.
 */
import type { ReactNode } from 'react';

/** Chart type for the widget visualization. */
export type ReportWidgetChartType =
    | 'bar'
    | 'pie'
    | 'donut'
    | 'area'
    | 'radar'
    | 'line';

/** Known optional fields on item.meta used by the composable widget (chart hover, non-interactive "Other"). */
export interface ReportWidgetItemMeta extends Record<string, unknown> {
    /** When set, row hover highlights this chart slice (e.g. "Other" for rows beyond top N). */
    chartItemId?: string;
    /** When true, pie/donut slice is not clickable (e.g. aggregated "Other" wedge). */
    isAggregatedOther?: boolean;
}

/** Single item shape consumed by both chart and table. */
export interface ReportWidgetItem {
    id: string;
    label: string;
    primaryValue: number | string;
    secondaryValues?: Record<string, number | string>;
    color?: string;
    /** Raw/report-specific data for adapters (e.g. drill-in, details panel). */
    meta?: ReportWidgetItemMeta;
}

/** Id used for chart highlight (row hover). Uses meta.chartItemId when present so "Other" rows map to the Other slice. */
export function getChartItemId(item: ReportWidgetItem): string {
    return item.meta?.chartItemId ?? item.id;
}

/** Column definition for the widget table. */
export interface ReportWidgetColumnConfig {
    id: string;
    label: string;
    /** Field key on ReportWidgetItem (e.g. primaryValue or key in secondaryValues). */
    valueKey: string;
    format?: 'currency' | 'number' | 'percent' | 'text';
    /** Column alignment. Default: right for number/currency, left otherwise. */
    align?: 'left' | 'right';
    /** Tooltip text for header (e.g. info icon). */
    headerTooltip?: string;
    /** Optional custom text color for highlighted cells (e.g. positive or warning). */
    highlightColor?: string;
    /** When true, cell text in this column is bold (e.g. first/label column). */
    bold?: boolean;
    /** Optional per-cell tooltip computed from the row item (e.g. list of properties). */
    getCellTooltip?: (item: ReportWidgetItem) => string | ReactNode | null;
    /** When true, clicking this column's cell will trigger a details_click interaction (if onDetailsClick is provided). */
    isDetailsTrigger?: boolean;
    /** Optional minimum rendered width for the column, in pixels. */
    minWidth?: number;
    /** Optional maximum rendered width for the column, in pixels. */
    maxWidth?: number;
    /** Allow text wrapping inside this column's cells. */
    allowWrap?: boolean;
    /** Optional line clamp when wrapping is enabled. */
    wrapLines?: number;
}

/** Normalized chart point used by widget visualizations. */
export interface ReportWidgetChartPoint {
    name: string;
    value: number;
    id: string;
    color?: string;
}

/** Interaction types for callbacks. */
export type ReportWidgetInteractionType =
    | 'row_click'
    | 'chart_item_click'
    | 'details_click';

/** Payload passed to report when user interacts (row, chart slice, details icon). */
export interface ReportWidgetInteractionPayload {
    widgetId: string;
    interactionType: ReportWidgetInteractionType;
    itemId: string;
    itemLabel: string;
    item: ReportWidgetItem;
    meta?: Record<string, unknown>;
}

/** Color palette for charts. */
export type ReportWidgetColorPalette = 'neutral' | 'positive' | 'negative';

/** Visualization config. */
export interface ReportWidgetVisualizationOptions {
    showLegend?: boolean;
    valueFormatter?: (value: number | string) => string;
    colors?: string[];
    palette?: ReportWidgetColorPalette;
    /** Minimum pie/donut slice angle in degrees; small slices render at this size for hover. Default 4. */
    pieMinAngle?: number;
    lineNames?: [string, string];
    secondSeriesKey?: string;
    /** Optional custom formatter for the x-axis tick labels. Signature matches Recharts tickFormatter. */
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: unknown[],
    ) => string;
}

export interface ReportWidgetVisualizationConfig {
    enabled: boolean;
    type: ReportWidgetChartType;
    options?: ReportWidgetVisualizationOptions;
}

/** Table config. */
export interface ReportWidgetTableConfigOptions {
    loadMoreLabel?: string;
    /** When set, table shows "Show N more" link instead of button; N = loadMoreCount. */
    loadMoreVariant?: 'button' | 'link';
    /** Number to show in "Show N more" link; passed by widget when hasMore. */
    loadMoreCount?: number;
    /** valueKey of the column that shows a color swatch (e.g. 'label'). */
    showColorSwatchInLabelColumn?: string;
    /** When set and items are empty, show this instead of default "No data". */
    emptyMessage?: string;
}

export interface ReportWidgetTableConfig {
    enabled: boolean;
    columns: ReportWidgetColumnConfig[];
    options?: ReportWidgetTableConfigOptions;
}

/** Table presentation options passed to ReportWidgetTable (config.options + derived showDetailsColumn). */
export interface ReportWidgetTableOptions
    extends ReportWidgetTableConfigOptions {}

/** Pagination strategy for the widget. */
export type ReportWidgetPagination =
    | {
          mode: 'none';
      }
    | {
          mode: 'client';
          pageSize: number;
          revealAllOnLoadMore?: boolean;
      }
    | {
          mode: 'server';
          pageSize: number;
          canLoadMore: boolean;
          onLoadMore: () => void;
      };

/** System-defined widget config (not user-editable in v0). */
export interface ReportWidgetConfig {
    widgetId: string;
    title: string;
    description?: string;
    preferredColumnSpan?: number;
    minWidth?: number;
    layout?: 'stacked' | 'sideBySide';
    hasTopLegend?: boolean;
    visualization: ReportWidgetVisualizationConfig;
    table?: ReportWidgetTableConfig;
    pagination?: ReportWidgetPagination;
    sortKey?: string;
    sortDirection?: 'asc' | 'desc';
    onRowClick?: (payload: ReportWidgetInteractionPayload) => void;
    onChartItemClick?: (payload: ReportWidgetInteractionPayload) => void;
    onDetailsClick?: (payload: ReportWidgetInteractionPayload) => void;
}
